import { AppAlert, AppDataTable, AppSearchBox, useCopyToClipboard } from '@9b3kuq5phwuk4txw/kiban-react-ui'
import { useOpportunityMonthlySales } from '@opportunities/$opportunityId/-hooks/useOpportunityMonthlySales'
import type { OpportunityMonthlySale } from 'backend/types'
import { useMemo, useState } from 'react'
import { getCurrentPeriod } from '@/lib/period-utils'
import { filterSales } from '../../-lib/sales-filter'
import { createSalesColumns, extractSalesValues, SALES_HEADERS } from './components/columns'

interface SalesTabProps {
  opportunityId: number
  /** ヘッダーが開いているか（外部から制御） */
  isHeaderOpen?: boolean
}

export function SalesTab({ opportunityId, isHeaderOpen = true }: SalesTabProps) {
  const [searchText, setSearchText] = useState('')
  const [showPeriodAlert, setShowPeriodAlert] = useState(true)
  const currentPeriod = getCurrentPeriod()

  // 売上一覧取得（現在の半期でフィルタリング）
  const { data, isLoading, error } = useOpportunityMonthlySales(opportunityId, {
    period: currentPeriod ?? undefined,
  })

  const handleCopy = useCopyToClipboard<OpportunityMonthlySale>(SALES_HEADERS, extractSalesValues)

  // カラム定義
  const columns = useMemo(() => createSalesColumns({ onCopy: handleCopy }), [handleCopy])

  // 検索フィルタリング
  const filteredSales = useMemo(() => filterSales(data?.sales ?? [], searchText), [data?.sales, searchText])

  const sales = data?.sales ?? []

  return (
    <div className='grid h-full grid-rows-[auto_minmax(0,1fr)]'>
      {/* ヘッダー部分（折りたたみ可能） */}
      {isHeaderOpen && (
        <div className='px-4'>
          {/* アラート */}
          <AppAlert type='error' className='mb-2'>
            現状のオポチュニティに紐づくsalesforceの売上状況です。
            報酬金額に関わる「紐づけ状況・オポ金額」は不確定なためご注意ください。
          </AppAlert>
          {showPeriodAlert && (
            <AppAlert type='info' className='mb-4' closable onClose={() => setShowPeriodAlert(false)}>
              今期「{currentPeriod}」の売上情報のみ表示されています。
            </AppAlert>
          )}

          <div className='mb-4'>
            <AppSearchBox
              label='売上情報を検索'
              value={searchText}
              onChange={setSearchText}
              placeholder='取引先名、案件名、IDで絞り込み'
            />
          </div>
        </div>
      )}

      {/* 売上一覧テーブル */}
      <AppDataTable
        data={filteredSales}
        columns={columns}
        isLoading={isLoading}
        errorMessage={error ? `売上情報の取得に失敗しました: ${error.message}` : undefined}
        pageSize={20}
        defaultSortKey='sales_id'
        defaultSortDirection='desc'
        getRowKey={(row) => row.id}
        emptyMessage={
          sales.length === 0 ? '売上情報はまだありません' : '検索条件に一致する売上情報が見つかりませんでした'
        }
        columnSelector={{
          cookieKey: 'header-select-opportunity-monthly-sales',
          targetColumnKey: 'action',
        }}
        containerClassName='pb-4'
      />
    </div>
  )
}
